/* eslint no-undef: "warn" */
import React, { useEffect, useState } from 'react'

// Main Chakra UI components:
import { Box, Button, Center, Image, Link, Text, VStack } from '@chakra-ui/react'

// External libs:
import { Link as RouteLink, useLocation } from 'react-router-dom'

import 'remixicon/fonts/remixicon.css'

// Local components and data:
import useMatchMedia from './MatchMedia'
import { Glossary } from './Glossary'
import Analytics from './Analytics'
import { NotePane, RadialImage, style } from './PlayersGuideUtil'
// Sections
import PlayersGuideIntro from './PlayersGuideIntro'
import PlayersGuideStarting from './PlayersGuideStarting'
import PlayersGuideFirstSteps from './PlayersGuideFirstSteps'
import PlayersGuideCombat from './PlayersGuideCombat'
import PlayersGuideMagic from './PlayersGuideMagic'
import PlayersGuidePoweringUp from './PlayersGuidePoweringUp'
import PlayersGuideEquipment from './PlayersGuideEquipment'
import PlayersGuideSneaking from './PlayersGuideSneaking'

const TableOfContents = () => {
    const isDesktop = useMatchMedia('(min-width:992px)', true)
    return (
        <Box mt={50} p={isDesktop ? 6 : 4} bg="#1a1a1a" borderRadius={12}>
            <Text fontSize="2xl" mb={4}><b>Table of Contents</b></Text>
            <VStack align="start" spacing={2}>
                <Link href="#starting-a-new-game" color="#88ccff">Starting a New Game</Link>
                <Link href="#preparing-for-adventure" color="#88ccff">Preparing for Adventure</Link>
                <Link href="#combat" color="#88ccff">Combat</Link>
                <Box pl={6}>
                    <VStack align="start" spacing={1}>
                        <Link href="#anatomy-of-a-round" color="#aaaaaa" fontSize="sm">Anatomy of a Round</Link>
                        <Link href="#performing-actions" color="#aaaaaa" fontSize="sm">Performing Actions</Link>
                        <Link href="#targetted-actions" color="#aaaaaa" fontSize="sm">Targetted Actions</Link>
                        <Link href="#positioning-and-cover" color="#aaaaaa" fontSize="sm">Positioning and Cover</Link>
                        <Link href="#recovering-from-combat" color="#aaaaaa" fontSize="sm">Recovering from Combat</Link>
                    </VStack>
                </Box>
                <Link href="#magic" color="#88ccff">Magic</Link>
                <Box pl={6}>
                    <VStack align="start" spacing={1}>
                        <Link href="#preparing-spells" color="#aaaaaa" fontSize="sm">Preparing Spells</Link>
                        <Link href="#casting-spells" color="#aaaaaa" fontSize="sm">Casting Spells</Link>
                        <Link href="#learning-new-spells" color="#aaaaaa" fontSize="sm">Learning New Spells</Link>
                    </VStack>
                </Box>
                <Link href="#exploring-and-powering-up" color="#88ccff">Exploring the World</Link>
                <Box pl={6}>
                    <VStack align="start" spacing={1}>
                        <Link href="#finding-and-exploring-dungeons" color="#aaaaaa" fontSize="sm">Finding and Exploring Dungeons</Link>
                        <Link href="#solo-mode" color="#aaaaaa" fontSize="sm">Splitting the Party: Going Solo</Link>
                    </VStack>
                </Box>
                <Link href="#equipment-and-items" color="#88ccff">Equipment and Items</Link>
                <Link href="#glossary" color="#88ccff">Glossary of Terms</Link>
            </VStack>
        </Box>
    )
}

const PlayersGuide = ({ onEnter, onExit }) => {
    // Handle GA location tracking
    const location = useLocation()
    useEffect(() => {
        Analytics.pageview(location, 'PlayersGuide')
        if (location.pathname === '/players-guide') {
            onEnter()
        }
    }, [location])

    useEffect(() => {
        return () => {
            onExit()
        }
    }, [])

    const isDesktopResolution = useMatchMedia('(min-width:992px)', true)

    return (
        <Center>
            {/* BACKGROUND */}
            <div
                style={{
                    color: '#cccccc',
                    backgroundColor: '#0f0f0f',
                    margin: isDesktopResolution ? 100 : 16,
                    maxWidth: style.maxBodyWidth,
                }}
            >
                {/*********************************************************************************************************
                 * HEADER
                 *********************************************************************************************************/}
                <Center mt={50}>
                    <Link as={RouteLink} to='/'>
                        <Image
                            src='/Logo.png'
                            alt='logo'
                            style={{
                                imageRendering: 'high-quality',
                                transition: 'all linear 50s',
                                height: isDesktopResolution ? '300px' : '200px',
                                maxWidth: '90%',
                                width: 'auto',
                            }}
                            opacity={0.9}
                        />
                    </Link>
                </Center>
                <Center mt={50}>
                    <VStack>
                        <Text fontSize={'xxl'}>
                            <b>Players Guide v1.7</b>
                        </Text>
                        <i>Under Construction! Not all information is complete or accurate!</i>
                        <i>Get more information and have your questions answered on our Discord server.</i>
                        <div/>
                        <Button
                            variant={'link'}
                            onClick={() => {
                                window.open('https://discord.gg/Td6ChnTzUH', '_blank')
                            }}
                        ><Image src='discord.png' width={'80%'} /></Button>
                    </VStack>
                </Center>

                <TableOfContents />

                {/*********************************************************************************************************
                 * CONTENT
                 *********************************************************************************************************/}

                <PlayersGuideIntro />

                <PlayersGuideStarting />

                <Center mt={style.defaultSpacing}>
                    <RadialImage src='darkforest03.png' />
                </Center>

                <PlayersGuideFirstSteps />

                <Center mt={style.defaultSpacing}>
                    <RadialImage src='darkforest04.png' />
                </Center>

                <PlayersGuideCombat />

                <Center mt={style.defaultSpacing}>
                    <RadialImage src='tome.png' />
                </Center>

                <PlayersGuideMagic />

                <Center mt={style.defaultSpacing}>
                    <RadialImage src='adventurers01.png' />
                </Center>

                <PlayersGuidePoweringUp />

                <Center mt={style.defaultSpacing}>
                    <RadialImage src='darkforest05.png' />
                </Center>

                <PlayersGuideEquipment />

                <Center mt={style.defaultSpacing}>
                    <RadialImage src='treasure.png' />
                </Center>

                <PlayersGuideSneaking />

                <Center mt={style.defaultSpacing}>
                    <RadialImage src='rogue01.png' />
                </Center>

                {/*********************************************************************************************************
                 * GLOSSARY
                 *********************************************************************************************************/}

                <Box mt={200} id="glossary">
                    <Text fontSize={'4xl'}>
                        <u>Glossary of Terms</u>
                    </Text>
                    <Glossary filter={null} setFilter={() => null} />
                </Box>
                <Box h={20} />
            </div>
        </Center>
    )
}

export default PlayersGuide
